import { getConfig } from './config';

/**
 * Single-flight loader for the Google Maps JavaScript API with the Places library.
 *
 * - Returns the same Promise on every call, so concurrent users of Maps share
 *   one script load.
 * - Uses `loading=async` and the recommended `callback=initGoogleMaps` pattern.
 * - If VITE_GOOGLE_MAPS_API_KEY is unset, resolves with `null` instead of
 *   throwing, so components can gracefully fall back to a plain input.
 */

let loadPromise = null;

export function loadGoogleMaps() {
    if (typeof window === 'undefined') return Promise.resolve(null);

    // Already loaded (e.g. hot reload)
    if (window.google?.maps?.places) {
        return Promise.resolve(window.google.maps);
    }

    if (loadPromise) return loadPromise;

    const apiKey = getConfig('GOOGLE_MAPS_API_KEY');
    if (!apiKey) {
        console.warn('[googleMapsLoader] GOOGLE_MAPS_API_KEY not configured — autocomplete disabled.');
        return Promise.resolve(null);
    }

    loadPromise = new Promise((resolve, reject) => {
        const cbName = '__initGoogleMaps__';
        window[cbName] = () => {
            delete window[cbName];
            resolve(window.google?.maps || null);
        };

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&loading=async&callback=${cbName}`;
        script.async = true;
        script.defer = true;
        script.onerror = () => {
            // Clean up so the next call gets a fresh attempt and we don't
            // leak the global callback or a dead <script> node in the DOM.
            delete window[cbName];
            script.remove();
            loadPromise = null;
            reject(new Error('Failed to load Google Maps script'));
        };
        document.head.appendChild(script);
    });

    return loadPromise;
}
